import {
  type CohortDslLegacy,
  type DerivedEventLegacy,
  type DSLValueLegacy,
  type IndexDateVariableLegacy,
  type SimpleEventLegacy,
  type SubjectVariableLegacy,
} from '@verantos-dev/types-cohorts';
import {
  getFullDslQueryString,
  transformDerivedEventToDsl,
  transformIndexDateToDsl,
  transformSimpleEventToDsl,
  transformSubjectVariableToDsl,
} from '@verantos-dev/util-cohort-definition-ui-to-dsl';

type CohortDefinitionQueryOptions = {
  newSubjectsVariable?: SubjectVariableLegacy;
  newSimpleEvent?: SimpleEventLegacy;
  newDerivedEvent?: DerivedEventLegacy;
  newIndexDateVariable?: IndexDateVariableLegacy;
};

/**
 * This function takes in a new cohort definition item (e.g. subject variable, simple event, etc.) within the options param
 * and returns a DSL query for the cohort definition.
 *
 * We generate a new cohort definition query by:
 * 1. Getting the current cohort definition DSL query
 * 2. Replacing the existing dsl property that maps to the cohort definition item's id with the new cohort definition item's dsl
 *
 * @param {CohortDefinitionQueryOptions} options - Options for the cohort definition query
 * @returns {string} - The cohort definition DSL query
 */
export const getCohortDefinitionQueryForEditModal = (
  currentCohortDSL: CohortDslLegacy,
  options: CohortDefinitionQueryOptions
): string => {
  /** Added validations to catch misuse of this function for developers guidance since the type is ambiguous on the number of options that can be provided */
  // check if options are provided, otherwise throw an error
  if (Object.keys(options).length === 0) {
    throw new Error('getCohortDefinitionQueryForEditModal: You must provide one option');
  }

  // check if only one option is provided, otherwise throw an error
  if (Object.keys(options).length > 1) {
    throw new Error(
      `getCohortDefinitionQueryForEditModal: only one option can be provided\nYou provided: ${Object.keys(
        options
      )}`
    );
  }

  /**
   * case: Subject Variable Option
   * generate a new cohort definition for a given subject variable
   */
  const { newSubjectsVariable } = options;
  if (newSubjectsVariable) {
    // calculate the new subject variable dsl
    const nextSubjectsVariableDsl = transformSubjectVariableToDsl(newSubjectsVariable);

    // check if the subject variable already exists in the cohort definition
    const subjectVariableIndex =
      currentCohortDSL?.subjectVariables &&
      currentCohortDSL.subjectVariables.findIndex(
        (subjectVariable) => subjectVariable.id === newSubjectsVariable.id
      );

    // if the subject variable exists, then we need to replace the existing subject variable dsl with the new subject variable dsl
    const nextSubjectsVariables: DSLValueLegacy[] = [];
    if (subjectVariableIndex > -1) {
      currentCohortDSL.subjectVariables.forEach((subjectVariable) =>
        nextSubjectsVariables.push(
          subjectVariable.id === newSubjectsVariable.id ? nextSubjectsVariableDsl : subjectVariable
        )
      );
    } else if (currentCohortDSL?.subjectVariables) {
      currentCohortDSL.subjectVariables.forEach((subjectVariable) =>
        nextSubjectsVariables.push(subjectVariable)
      );
      nextSubjectsVariables.push(nextSubjectsVariableDsl);
    } else {
      nextSubjectsVariables.push(nextSubjectsVariableDsl);
    }

    // set the new cohort definition dsl
    const nextCohortDSL: CohortDslLegacy = {
      simpleEvents: currentCohortDSL?.simpleEvents,
      derivedEvents: currentCohortDSL?.derivedEvents || [],
      indexDate: currentCohortDSL?.indexDate,
      subjectVariables: nextSubjectsVariables,
      studyPeriodRestrictions: currentCohortDSL?.studyPeriodRestrictions,
      inclusionCriteria: currentCohortDSL?.inclusionCriteria || [],
      exclusionCriteria: currentCohortDSL?.exclusionCriteria || [],
    };

    // return the new cohort definition dsl with mocks of empty items for code completion
    return getFullDslQueryString(
      nextCohortDSL,
      true // enable mocking to obtain a complete DSL query string
    );
  }

  /**
   * case: Index Date Variable Option
   * generate a new cohort definition for a given index date variable
   */
  const { newIndexDateVariable } = options;
  if (newIndexDateVariable) {
    // calculate the new index date variable dsl
    const nextIndexDateVariableDsl = transformIndexDateToDsl(newIndexDateVariable);

    // set the new cohort definition dsl
    const nextCohortDSL: CohortDslLegacy = {
      simpleEvents: currentCohortDSL?.simpleEvents,
      derivedEvents: currentCohortDSL?.derivedEvents || [],
      indexDate: nextIndexDateVariableDsl,
      subjectVariables: currentCohortDSL?.subjectVariables || [],
      studyPeriodRestrictions: currentCohortDSL?.studyPeriodRestrictions,
      inclusionCriteria: currentCohortDSL?.inclusionCriteria || [],
      exclusionCriteria: currentCohortDSL?.exclusionCriteria || [],
    };

    // return the new cohort definition dsl with mocks of empty items for code completion
    return getFullDslQueryString(
      nextCohortDSL,
      true // enable mocking to obtain a complete DSL query string
    );
  }

  /**
   * case: Derived Event Option
   * generate a new cohort definition for a given derived event
   */
  const { newDerivedEvent } = options;
  /** generate a new cohort definition for a given simple event */
  if (newDerivedEvent) {
    // calculate the new simple event dsl
    const nextDerivedEventDsl = transformDerivedEventToDsl(newDerivedEvent);

    // check if the simple event already exists in the cohort definition
    const derivedEventIndex =
      currentCohortDSL?.derivedEvents &&
      currentCohortDSL.derivedEvents.findIndex(
        (simpleEvent) => simpleEvent.id === newDerivedEvent.id
      );

    // if the simple event exists, then we need to replace the existing simple event dsl with the new simple event dsl
    const nextDerivedEvents: DSLValueLegacy[] = [];
    if (derivedEventIndex > -1) {
      currentCohortDSL.derivedEvents.forEach((derivedEvent) =>
        nextDerivedEvents.push(
          derivedEvent.id === newDerivedEvent.id ? nextDerivedEventDsl : derivedEvent
        )
      );
    } else if (currentCohortDSL?.derivedEvents) {
      nextDerivedEvents.push(...currentCohortDSL.derivedEvents);
      nextDerivedEvents.push(nextDerivedEventDsl);
    } else {
      nextDerivedEvents.push(nextDerivedEventDsl);
    }

    // set the new cohort definition dsl
    const nextCohortDSL: CohortDslLegacy = {
      simpleEvents: currentCohortDSL?.simpleEvents || [],
      derivedEvents: nextDerivedEvents,
      indexDate: currentCohortDSL?.indexDate,
      subjectVariables: currentCohortDSL?.subjectVariables || [],
      studyPeriodRestrictions: currentCohortDSL?.studyPeriodRestrictions,
      inclusionCriteria: currentCohortDSL?.inclusionCriteria || [],
      exclusionCriteria: currentCohortDSL?.exclusionCriteria || [],
    };

    // return the new cohort definition dsl with mocks of empty items for code completion
    return getFullDslQueryString(
      nextCohortDSL,
      true // enable mocking to obtain a complete DSL query string
    );
  }

  /**
   * case: Simple Event Option
   * generate a new cohort definition for a given simple event
   */
  const { newSimpleEvent } = options;
  /** generate a new cohort definition for a given simple event */
  if (newSimpleEvent) {
    // calculate the new simple event dsl
    const nextSimpleEventDsl = transformSimpleEventToDsl(newSimpleEvent);

    // check if the simple event already exists in the cohort definition
    const simpleEventIndex =
      currentCohortDSL?.simpleEvents &&
      currentCohortDSL.simpleEvents.findIndex(
        (simpleEvent) => simpleEvent.id === newSimpleEvent.id
      );

    // if the simple event exists, then we need to replace the existing simple event dsl with the new simple event dsl
    const nextSimpleEvents: DSLValueLegacy[] = [];
    if (simpleEventIndex > -1) {
      currentCohortDSL.simpleEvents.forEach((simpleEvent) =>
        nextSimpleEvents.push(
          simpleEvent.id === newSimpleEvent.id ? nextSimpleEventDsl : simpleEvent
        )
      );
    } else if (currentCohortDSL?.simpleEvents) {
      currentCohortDSL.simpleEvents.forEach((simpleEvent) => nextSimpleEvents.push(simpleEvent));
      nextSimpleEvents.push(nextSimpleEventDsl);
    } else {
      nextSimpleEvents.push(nextSimpleEventDsl);
    }

    // set the new cohort definition dsl
    const nextCohortDSL: CohortDslLegacy = {
      simpleEvents: nextSimpleEvents,
      derivedEvents: currentCohortDSL?.derivedEvents || [],
      indexDate: currentCohortDSL?.indexDate,
      subjectVariables: currentCohortDSL?.subjectVariables || [],
      studyPeriodRestrictions: currentCohortDSL?.studyPeriodRestrictions,
      inclusionCriteria: currentCohortDSL?.inclusionCriteria || [],
      exclusionCriteria: currentCohortDSL?.exclusionCriteria || [],
    };

    // return the new cohort definition dsl with mocks of empty items for code completion
    return getFullDslQueryString(
      nextCohortDSL,
      true // enable mocking to obtain a complete DSL query string
    );
  }

  return currentCohortDSL
    ? getFullDslQueryString(
        currentCohortDSL,
        true // enable mocking to obtain a complete DSL query string
      )
    : '';
};
